import Form from '@rjsf/core';
import createPrecompiledValidator from './rjsf/createPrecompiledValidator';
import { useMemo } from 'react';
import { useSchemaViewerContext } from './SchemaContext';

type ValidatorFn = ((data: unknown) => boolean) & { errors?: unknown };

// Load generated precompiled validators (if present). Missing entries are
// treated as hard errors so we never fall back to runtime AJV compilation.
const precompiledValidatorFns: Record<string, Record<string, ValidatorFn>> = (() => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('./precompiled');
    return mod.precompiledValidatorFns || {};
  } catch (err) {
    console.error('Failed to load precompiled validators from ./precompiled', err);
    return {};
  }
})();

export const SchemaViewer = () => {
  const { schema, selectedRegistry, error, formData, setFormData } = useSchemaViewerContext()

  const uiSchema = {
    'ui:submitButtonOptions': {
      submitText: 'Validate',
      props: {
        className: 'btn btn-primary' 
      }
    }
  };

  const log = (type: string) => console.log.bind(console, type);

  const handleChange = (data: any) => {
    setFormData(data.formData);
  };

  // Build a precompiled validator for the current schema. This avoids the
  // runtime `new Function()` call AJV performs when compiling at runtime,
  // which is what required `'unsafe-eval'` in the CSP.
  const { validator, validatorError } = useMemo(() => {
    if (!schema) {
      return { validator: null, validatorError: null };
    }

    const schemaId = (schema as { $id?: string }).$id;
    if (!schemaId) {
      return {
        validator: null,
        validatorError: 'Selected schema is missing $id, so no precompiled validator can be loaded.',
      };
    }

    const fns = precompiledValidatorFns[schemaId];
    if (!fns) {
      return {
        validator: null,
        validatorError: `No precompiled validator found for schema $id "${schemaId}". Run yarn precompile-validators.`,
      };
    }

    try {
      return {
        validator: createPrecompiledValidator(fns as never, schema),
        validatorError: null,
      };
    } catch {
      return {
        validator: null,
        validatorError: `Failed to construct precompiled validator for schema $id "${schemaId}".`,
      };
    }
  }, [schema]);

  const displayError = error || validatorError;

  return (
    <div style={{ padding: '0 2rem 2rem 2rem' }}>
      <h2>Cutover Schema Viewer</h2>
      {displayError && <p className="alert alert-danger">Error: {displayError}</p>}
      {schema && validator ? (
        <>
          {selectedRegistry && (
            <h3>{selectedRegistry.title} – v{selectedRegistry.version}</h3>
          )}
          <Form
            schema={schema}
            formData={formData}
            uiSchema={uiSchema}
            validator={validator}
            onChange={handleChange}
            onSubmit={log('submitted')}
            onError={log('errors')}
          />
        </>
      ) : (
        !displayError && <p>Please select a schema type to begin.</p>
      )}
    </div>
  );
};